//
// ON句が以下の形式になることを想定した型。
// "<parentNode.name>"."<parentNodeJoinColumn>" = "currentNode.name"."currentNodeJoinColumn"
// これ以外の形式になるような特殊なON句については、一旦考慮しない。もし今後発生した場合、union typeを作る予定。

import { z } from 'zod'

import { nodePathSchema, propertyNameWithStreamNameSchema } from './common.ts'
import { viewQueryFilterNodeSchema } from './filter.ts'

//
const viewQueryNodeReadJoinOnByIdSchema = z.object({
  type: z.literal('id'),
  parentNodeName: z.string(),
  parentNodeColumnName: z.string(),
  currentNodeName: z.string(),
  currentNodeColumnName: z.string(),
  meta: z.object({
    parentNodeTableSql: z.string(),
    parentNodeLabel: z.string(),
    parentNodeColumnLabel: z.string(),
    parentNodeStreamName: z.string(),
    parentNodePropertyName: z.string(),
    currentNodeTableSql: z.string(),
    currentNodeLabel: z.string(),
    currentNodeColumnLabel: z.string(),
    currentNodeStreamName: z.string(),
    currentNodePropertyName: z.string(),
  }),
})
const viewQueryNodeReadJoinOnByCustomSqlSchema = z.object({
  type: z.literal('sql'),
  sql: z.string(),
})
const viewQueryNodeReadJoinOnSchema = z.union([
  viewQueryNodeReadJoinOnByIdSchema,
  viewQueryNodeReadJoinOnByCustomSqlSchema,
])
export type ViewQueryNodeReadJoinOnId = z.infer<typeof viewQueryNodeReadJoinOnByIdSchema>
export type ViewQueryNodeReadJoinOn = z.infer<typeof viewQueryNodeReadJoinOnSchema>

export const tableSqlTransparencySchema = z.enum(['transparent', 'opaque'])
export type TableSqlTransparency = z.infer<typeof tableSqlTransparencySchema>

const viewQueryNodeReadSchema = z.object({
  tableSql: z.string(), // 通常は単にテーブル名。サブクエリでも可能。
  // tableSql の透過性。transparent = 基テーブルの行を絞る・素通しするだけの行フィルタ形（出力の各行が基テーブルの行そのままで、行述語を外側から AND しても内側注入と同値）、opaque = そう示せないすべて。
  // SQL の用語ではなく opaque type と同じ黒箱契約の意味（SQL 側の近縁概念は updatable view の判定条件）。tableType（SQL 配置戦略）とは直交する軸
  tableSqlTransparency: tableSqlTransparencySchema,
  tableType: z.enum(['table', 'subquery', 'lateral_subquery']),
  idColumn: z.string(), // 今後optionalにする予定だが、当分は確実に入るはず
  join: z
    .object({
      // 子ノードのとき、必ず存在する
      relation: z.enum(['one_to_many', 'many_to_one', 'one_to_one']),
      joinType: z.enum(['LEFT JOIN', 'RIGHT JOIN', 'FULL OUTER JOIN', 'CROSS JOIN', 'INNER JOIN', 'LEFT JOIN LATERAL']),
      joinOn: viewQueryNodeReadJoinOnSchema, // id = user_idのような基本的な結合条件。さらに絞り込み条件を指定したい場合はjoinFilterTreeで記載する。
      search: z.object({
        // レコードを検索する際に使う
        sql: z.string(), // value,labelの2つのフィールドを返すSQL
        dependedPropertyNames: propertyNameWithStreamNameSchema.array(),
      }),
      joinFilterTree: viewQueryFilterNodeSchema.optional(), // 結合した後に絞り込みたい場合。UI的にjoinOnと分けて表示したいので、ここでも分けておく
    })
    .optional(),
})

export const viewQueryNodeSchema = z.object({
  // 木構造
  // eslint-disable-next-line sonarjs/todo-tag
  name: z.string(), // uniqueなノード名。readのasで使われる(SQL発行に関するフィールドはreadにまとめているが、これだけ例外?) TODO
  path: nodePathSchema, // 親からのパスを配列に格納した値
  get children() {
    return viewQueryNodeSchema.array().optional()
  },
  read: viewQueryNodeReadSchema,
  write: z
    .object({
      streamName: z.string(), // 書き込み時に直接使われることはないが、フォームUIのロジックを完結にするため永続化する? あっても特に違和感ないので
      parentIdColumn: z.string().optional(), // 子ノードであり、かつ書き込み可能にしたいとき、親レコードIDを格納しているカラムを指定
    })
    .optional(),
  // UI表示で使う情報
  meta: z.object({
    label: z.string(),
    // streamとの依存関係
    dependedStreamNames: z.string().array(), // 依存しているstream名。subqueryでない限りは1つのみ
  }),
})
export type ViewQueryNode = z.infer<typeof viewQueryNodeSchema>

// ViewQueryScalar.parseValue は無検証 pass-through のため、runtime 値は TS 型を破りうる。
// 欠損・未知値を安全側へ倒すため、transparent の positive match 以外は opaque とする。
export const getTableSqlTransparency = (read: ViewQueryNode['read']): TableSqlTransparency =>
  read.tableSqlTransparency === 'transparent' ? 'transparent' : 'opaque'
